/* eslint-disable react-hooks/exhaustive-deps */
import LoadableContainer from '@components/LoadableContainer';
import { Button, Card, Col, Divider, Empty, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import UploadImageModal from './UploadImageModal';
import StudioImageItemCard from './StudioImageItemCard';
import pluralize from 'pluralize';
import StudioImageItemDetail from './StudioImageItemDetail';
import { CloseOutlined } from '@ant-design/icons';
import { FileImageItem } from '@core/features';
import { useAppDispatch, useAuthState, useStudioState } from '@core/configureStore';
import { fetchGalleryImages, uploadStudioPhotos } from '@core/features/studio/thunks';
import { uploadImage } from '@services/google-photo-service';
import { StudioPhoto, StudioPhotoStage } from '@core/models';
import SplashScreen from '@components/SplashScreen';
import { getStatusData } from './RenderStudioPhotoStatus';

type Props = {};

const ImageGalleryTab = (props: Props) => {
  const dispatch = useAppDispatch();
  const { user } = useAuthState();
  const [openUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [selectedImageItem, setSelectedImageItem] = useState<StudioPhoto | undefined>(undefined);
  const { studioPhotos, isLoading } = useStudioState();

  const handleUploadImage = () => {
    setOpenUploadModal(true);
  };

  const handleImageItemClicked = (item: StudioPhoto) => {
    setSelectedImageItem(item);
  };

  const handleCloseDetail = () => {
    setSelectedImageItem(undefined);
  };

  const handleOnUpload = async (uploadedItems: FileImageItem[]) => {
    if (!user) return;
    const items: FileImageItem[] = await Promise.all(
      uploadedItems.map(async item => {
        const uploadToken = await uploadImage(item.extension, item.data);
        return { ...item, uploadToken: uploadToken };
      })
    );
    await dispatch(
      uploadStudioPhotos({
        locationId: user?.selected_location.location_id,
        fileImageItems: items,
      })
    );
    await handleFetchImages();
  };

  const handleFetchImages = async () => {
    if (!user) return;
    await dispatch(fetchGalleryImages({ locationId: user?.selected_location.location_id }));
  };

  useEffect(() => {
    const init = async () => {
      await handleFetchImages();
    };
    init();
  }, [user?.selected_location]);

  return (
    <React.Fragment>
      <LoadableContainer isLoading={isLoading} loadComponent={<SplashScreen />}>
        <React.Fragment>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p>
              There are {studioPhotos.length} {pluralize('photo', studioPhotos.length)} in the
              gallery
            </p>
            <Button onClick={handleUploadImage}>Upload image</Button>
          </div>
          <Divider />
          <Row gutter={20}>
            <Col span={selectedImageItem ? 17 : 24} style={{ height: '100%', overflow: 'auto' }}>
              {[
                {
                  sectionName: 'Processing',
                  photoItems: studioPhotos.filter(
                    photo => getStatusData(photo) === StudioPhotoStage.Processing
                  ),
                },
                {
                  sectionName: 'Other photos',
                  photoItems: studioPhotos.filter(
                    photo => getStatusData(photo) !== StudioPhotoStage.Processing
                  ),
                },
              ].map(section => (
                <React.Fragment>
                  <div style={{ marginTop: 20 }}>
                    <h2>{section.sectionName}</h2>
                    <Divider />
                    <LoadableContainer
                      isLoading={section.photoItems.length === 0}
                      loadComponent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={<span>No photos found</span>}
                        />
                      }>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          flexWrap: 'wrap',
                        }}>
                        {section.photoItems.map(studioPhotos => (
                          <StudioImageItemCard
                            onClick={item => handleImageItemClicked(item)}
                            item={studioPhotos}
                          />
                        ))}
                      </div>
                    </LoadableContainer>
                  </div>
                </React.Fragment>
              ))}
            </Col>
            {selectedImageItem && (
              <Col span={7}>
                <Card
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    height: '100vh',
                    overflow: 'auto',
                    boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                  }}
                  title={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <h3>Image Detail</h3>
                      <CloseOutlined onClick={handleCloseDetail} />
                    </div>
                  }>
                  <LoadableContainer
                    isLoading={!selectedImageItem}
                    loadComponent={<div>No image selected</div>}>
                    <StudioImageItemDetail
                      onClose={() => setSelectedImageItem(undefined)}
                      mediaId={selectedImageItem.media_id}
                    />
                  </LoadableContainer>
                </Card>
              </Col>
            )}
          </Row>
        </React.Fragment>
      </LoadableContainer>
      <UploadImageModal
        onUpload={handleOnUpload}
        open={openUploadModal}
        setOpen={open => setOpenUploadModal(open)}
      />
    </React.Fragment>
  );
};

export default ImageGalleryTab;
